import { useRef } from 'react';
import { useVirtual } from 'react-virtual';

import { Add16, Close16 } from 'ui/icons';
import { GhostButton } from 'ui/button';
import {
  Container,
  FractionalInputContainer,
  HeaderContainer,
  InputsContainer,
  StyledFractionalGhostErrorButton,
} from './InputList.styled';
import { FractionalInputBox } from './FractionalInputBox';
import type { Fraction } from '../../../types';

type FractionalProps = {
  inputList: { value: string; label: string };
  fractionsList?: Array<Fraction>;
  handleChange: (props: { name: string; fractionsList: Array<Fraction> }) => void;
};

/**
 * A list of inputs used to toggle the state of fractional feature flags.
 */
export const FractionalInputList = ({
  fractionsList = [],
  handleChange,
  inputList,
}: FractionalProps) => {
  const addInputRow = () => {
    handleChange({
      name: inputList.value,
      fractionsList: [...fractionsList, { value: '', percentageValue: 1 }],
    });
  };

  const onChange = (
    newValue: Fraction['value'],
    newPercentageValue: Fraction['percentageValue'],
    index: number,
  ) => {
    const changedFractionsList = fractionsList.map((val, i) => {
      return i === index ? { value: newValue, percentageValue: newPercentageValue } : val;
    });

    handleChange({ name: inputList.value, fractionsList: changedFractionsList });
  };

  const removeInputRow = (index: number) => {
    const newFractionsList = fractionsList.filter((_, i) => i !== index);
    handleChange({ name: inputList.value, fractionsList: newFractionsList });
  };

  const isOrg = inputList.label.toLowerCase().includes('org');

  const parentRef = useRef<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtual({
    size: fractionsList.length,
    parentRef,
  });

  return (
    <Container>
      <HeaderContainer>{inputList.label}</HeaderContainer>
      <InputsContainer ref={parentRef}>
        <div
          style={{
            height: rowVirtualizer.totalSize,
          }}
        >
          {rowVirtualizer.virtualItems.map(({ index, start, measureRef, key }) => {
            const fraction = fractionsList[index];
            const style = {
              position: 'absolute' as any,
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${start}px)`,
            };
            return (
              <div key={key} ref={measureRef} style={style}>
                <FractionalInputContainer key={index} data-test="input-list-row">
                  <FractionalInputBox
                    {...fraction}
                    isOrg={isOrg}
                    onChange={(newValue, newPercentageValue) =>
                      onChange(newValue, newPercentageValue, index)
                    }
                  />
                  <StyledFractionalGhostErrorButton
                    data-test="input-list-remove-row"
                    onClick={() => removeInputRow(index)}
                  >
                    <Close16 />
                  </StyledFractionalGhostErrorButton>
                </FractionalInputContainer>
              </div>
            );
          })}
        </div>
      </InputsContainer>
      <GhostButton onClick={addInputRow} data-test="input-list-add-row">
        <Add16 />
        &nbsp;Add {isOrg ? 'org' : 'project'}
      </GhostButton>
    </Container>
  );
};
